import { Link } from 'react-router-dom'
import { SparklesIcon } from '@heroicons/react/24/outline'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    product: [
      { label: '生成星盘', href: '/chart' },
      { label: '我的星盘', href: '/user' },
      { label: '分享社区', href: '/shares' },
    ],
    resources: [
      { label: '使用指南', href: '/guide' },
      { label: 'API 文档', href: '/api' },
      { label: '常见问题', href: '/faq' },
    ],
    about: [
      { label: '关于我们', href: '/about' },
      { label: '隐私政策', href: '/privacy' },
      { label: '服务条款', href: '/terms' },
    ],
  }

  const socialLinks = [
    { icon: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>, href: 'https://github.com', label: 'GitHub' },
    { icon: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>, href: 'https://twitter.com', label: 'Twitter' },
    { icon: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>, href: 'mailto:contact@7e.ink', label: 'Email' },
  ]

  return (
    <footer className="relative bg-cyber-darker border-t border-cyber-blue/10">
      {/* 顶部发光效果 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-blue/30 to-transparent" />
      
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyber-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyber-purple/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 主要内容 */}
        <div className="py-12 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Logo & 简介 */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 group mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-cyber-blue rounded-lg blur opacity-50 group-hover:opacity-80 transition-opacity" />
                <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-cyber-blue to-cyber-purple flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-cyber-black" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-gradient font-cyber">ASTRO</span>
                <span className="text-[10px] text-cyber-blue/60 -mt-1 tracking-widest font-mono">SYSTEM</span>
              </div>
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed">
              基于出生信息的赛博科技星盘分析，融合 3D 可视化与 AI 解读，探索你的数字人格。
            </p>
            
            {/* 社交链接 */}
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-cyber-dark border border-cyber-blue/20 flex items-center justify-center text-gray-400 hover:text-cyber-blue hover:border-cyber-blue/50 hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all"
                  aria-label={social.label}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* 产品链接 */}
          <div>
            <h3 className="text-sm font-cyber font-semibold text-white mb-4 tracking-wide">
              产品
            </h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-500 hover:text-cyber-blue transition-colors relative group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-cyber-blue group-hover:w-full transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 资源链接 */}
          <div>
            <h3 className="text-sm font-cyber font-semibold text-white mb-4 tracking-wide">
              资源
            </h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-500 hover:text-cyber-blue transition-colors relative group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-cyber-blue group-hover:w-full transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 关于链接 */}
          <div>
            <h3 className="text-sm font-cyber font-semibold text-white mb-4 tracking-wide">
              关于
            </h3>
            <ul className="space-y-3">
              {footerLinks.about.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-500 hover:text-cyber-blue transition-colors relative group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-cyber-blue group-hover:w-full transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 分隔线 */}
        <div className="cyber-divider" />

        {/* 底部版权 */}
        <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>© {currentYear} astro.7e.ink</span>
            <span className="hidden md:inline text-gray-700">|</span>
            <span className="hidden md:inline font-mono text-cyber-blue/50">
              v1.0.0
            </span>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyber-green animate-pulse" />
              系统运行正常
            </span>
            <span className="text-gray-700">|</span>
            <a
              href="https://7e.ink"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-cyber-blue transition-colors"
            >
              七亿导航
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
